/**
 * InteractiveEconomicsPanel - Live financial modeling for shared comparison views
 *
 * Renders editable deal parameters (ESA pricing, discount rate, CapEx, VPP)
 * per-scenario, with live recomputation of IRR, NPV, payback, and cash flows.
 * Used in embedded mode (shared links) — no account needed, changes are ephemeral.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Switch,
    FormControlLabel,
    Checkbox,
    Collapse,
    IconButton,
    Button,
    Slider,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    TrendingUp as TrendingUpIcon,
    Timeline as TimelineIcon,
    Speed as SpeedIcon,
    AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
} from 'recharts';
import { vortxTypography, useThemeMode } from '@vortx/shared-ui';
import { FinancialCalculator } from '../FinancialAnalysis';

// Scenario colors (match ScenarioCompareModalV2)
const SCENARIO_COLORS = [
    '#b28c50', '#5fd4c4', '#8b9dc3', '#c4a35a', '#6b9080',
];

const PRICING_MODEL_LABELS = {
    fixed_rate: 'Fixed Rate ($/kWh)',
    savings_share: 'Savings Share (%)',
    fixed_monthly: 'Fixed Fee ($/year)',
    capacity_payment: 'Capacity ($/kW/mo)',
};

const DEFAULT_OM_PER_KW_YEAR = 15;
const DEFAULT_INSURANCE_PERCENT = 0.005;
const DEFAULT_ASSET_MGMT_PER_YEAR = 2400;
const DEFAULT_ITC_ELIGIBLE_FRACTION = 0.9;
const ITC_RATE = 0.30;

// Format currency consistently
const fmtCurrency = (val, useParens = false) => {
    if (val == null || isNaN(val)) return '—';
    const absVal = Math.abs(val);
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(absVal);
    if (val < 0) return useParens ? `(${formatted})` : `-${formatted}`;
    return formatted;
};

const fmtPercent = (val, decimals = 1) => {
    if (val == null || isNaN(val)) return '—';
    return `${(val * 100).toFixed(decimals)}%`;
};

// Extract initial editable params from a scenario's dealAnalysisConfig
// Defaults per ESA model — only used when no saved config exists
const ESA_MODEL_DEFAULTS = {
    fixed_rate: { rate: 0.12 },
    savings_share: { sharePercent: 0.80 },
    fixed_monthly: { payment: 42000 },       // yearly (converted from monthly * 12)
    capacity_payment: { ratePerKw: 12 },
};

function extractInitialParams(scenario) {
    const dealConfig = scenario.ui_state?.form?.dealAnalysisConfig || {};
    const esaConfig = dealConfig.esaConfig || {};
    const capexConfig = dealConfig.capexConfig || {};
    const model = esaConfig.model || 'fixed_rate';
    const defaults = ESA_MODEL_DEFAULTS[model] || {};

    return {
        dealStructure: dealConfig.viewMode === 'capex' ? 'capex' : 'esa',
        // ESA params — use saved values, fall back to model-appropriate defaults
        esaModel: model,
        esaRate: esaConfig.rate ?? defaults.rate ?? 0.12,
        esaTerm: esaConfig.term ?? 20,
        esaEscalation: esaConfig.escalation ?? 0.025,
        esaSharePercent: esaConfig.sharePercent ?? defaults.sharePercent ?? 0.80,
        esaPayment: esaConfig.payment != null ? esaConfig.payment * 12 : (defaults.payment ?? 42000),
        esaRatePerKw: esaConfig.ratePerKw ?? defaults.ratePerKw ?? 12,
        // Shared params
        discountRate: dealConfig.discountRate ?? 0.08,
        includeVpp: dealConfig.vppIncluded || false,
        vppDurationYears: dealConfig.vppDurationYears ?? 10,
        vppParticipationMode: dealConfig.computedResults?.vppDetail?.participationMode || 'aggregator',
        vppAggregatorCutPct: dealConfig.computedResults?.vppDetail?.aggregatorCutPct ?? 20,
        // CapEx params
        capexMarkupPercent: capexConfig.markupPercent ?? 20,
        customerItcEligible: capexConfig.customerItcEligible !== false,
        capexEscalationRate: capexConfig.escalationRate ?? 0.03,
        capexTerm: capexConfig.term ?? 25,
    };
}

// Extract common scenario data needed by both ESA and CapEx computations
function extractScenarioData(scenario, helpers) {
    const { getSystemConfig, getFinancialSummary, getCapexBreakdown, getOpexBreakdown, getDealAnalysis, monthlyPerformanceData } = helpers;

    const systemConfig = getSystemConfig(scenario);
    const financial = getFinancialSummary(scenario);
    const capex = getCapexBreakdown(scenario);
    const opex = getOpexBreakdown(scenario);

    const systemCost = capex?.total || financial.totalCapex || 0;
    const itcAmount = capex?.itc_amount || 0;

    const latestRun = (scenario.optimization_runs || []).filter(r => !r.variant_type).slice(-1)[0];
    const outputs = latestRun?.results?.outputs || {};
    const annualProductionKwh = outputs.PV?.annual_energy_produced_kwh || 0;
    const annualSavings = financial.annualSavings || 0;
    const totalCapacityKw = (systemConfig.pvKw || 0) + (systemConfig.storageKw || 0) + (systemConfig.generatorKw || 0);

    // DER charges and fuel costs from monthly performance
    const monthly = monthlyPerformanceData?.[scenario.id];
    let year1DerCharges = 0;
    let year1FuelCost = 0;
    if (monthly?.monthly_der_charges?.length > 0) {
        year1DerCharges = monthly.monthly_der_charges.reduce(
            (sum, m) => sum + (m.standby_cost || 0) + (m.departing_load_cost || 0), 0
        );
    }
    if (monthly?.asset_performance?.length > 0) {
        year1FuelCost = monthly.asset_performance.reduce(
            (sum, m) => sum + (m.generator_fuel_cost || 0), 0
        );
    }

    // BAU rate from getDealAnalysis
    const existingDeal = getDealAnalysis(scenario);
    const bauRate = existingDeal?.bauRate || 0;

    // VPP data — save both gross estimates and full detail for participation mode toggling
    const computed = scenario.ui_state?.form?.dealAnalysisConfig?.computedResults;
    const vppDetail = computed?.vppDetail || null;
    // Gross VPP midpoint (before aggregator cut) for live recomputation
    const vppGrossMidpoint = vppDetail
        ? ((vppDetail.grossLow || 0) + (vppDetail.grossHigh || 0)) / 2
        : (computed?.vppEstimateAnnual || computed?.vppAnnualRevenue || 0);

    return {
        systemConfig, financial, capex, opex, systemCost, itcAmount,
        annualProductionKwh, annualSavings, totalCapacityKw,
        year1DerCharges, year1FuelCost, bauRate, vppGrossMidpoint, vppDetail,
    };
}

// Compute ESA results
function computeEsaResults(params, data) {
    const {
        systemCost, itcAmount, annualProductionKwh, annualSavings, totalCapacityKw,
        systemConfig, opex, year1DerCharges, year1FuelCost, bauRate, vppGrossMidpoint,
    } = data;
    if (systemCost <= 0) return null;

    const esaPricing = {
        model: params.esaModel, term: params.esaTerm, escalation: params.esaEscalation,
    };
    switch (params.esaModel) {
        case 'fixed_rate': esaPricing.rate = params.esaRate; break;
        case 'savings_share': esaPricing.sharePercent = params.esaSharePercent; break;
        case 'fixed_monthly': esaPricing.payment = params.esaPayment / 12; break;
        case 'capacity_payment': esaPricing.ratePerKw = params.esaRatePerKw; break;
    }

    try {
        const esaRevenue = FinancialCalculator.calculateEsaRevenue(
            esaPricing, annualProductionKwh, annualSavings, totalCapacityKw
        );

        const esaExpenses = FinancialCalculator.calculateEsaExpenses(
            {
                pvKw: systemConfig.pvKw || 0,
                batteryKwh: systemConfig.storageKwh || 0,
                generatorKw: systemConfig.generatorKw || 0,
            },
            {
                omPerKwYear: opex?.pvOM ? opex.pvOM / (systemConfig.pvKw || 1) : DEFAULT_OM_PER_KW_YEAR,
                omPerKwhYear: opex?.batteryOM ? opex.batteryOM / (systemConfig.storageKwh || 1) : 5,
                omPerKwGenYear: opex?.generatorOM ? opex.generatorOM / (systemConfig.generatorKw || 1) : 20,
                insurancePercent: DEFAULT_INSURANCE_PERCENT,
                assetMgmtPerYear: DEFAULT_ASSET_MGMT_PER_YEAR,
            },
            systemCost,
            params.esaTerm
        );

        // Apply participation mode to gross VPP revenue
        const vppRevenueFactor = params.vppParticipationMode === 'direct' ? 1.0 : (100 - params.vppAggregatorCutPct) / 100;
        const vppAnnualRevenue = params.includeVpp ? vppGrossMidpoint * vppRevenueFactor : 0;
        const energyEsc = 0.03;

        const annualCashFlows = esaRevenue.annual.map((rev, i) => {
            const exp = esaExpenses.annual[i];
            const year = i + 1;
            let vppRev = 0;
            if (vppAnnualRevenue > 0 && year <= params.vppDurationYears) {
                vppRev = vppAnnualRevenue * Math.pow(1 + energyEsc, i);
            }
            return rev.revenue + vppRev - exp.total;
        });

        const developerReturns = FinancialCalculator.calculateDeveloperReturns(
            systemCost, annualCashFlows, itcAmount, params.discountRate
        );

        // Customer value
        const esaPaymentsArray = esaRevenue.annual.map(r => r.revenue);
        const annualUtilitySavingsArray = Array(params.esaTerm).fill(0).map((_, i) =>
            annualSavings * Math.pow(1 + energyEsc, i)
        );

        const customerAnnualCashflows = annualUtilitySavingsArray.map((savings, i) => {
            const escalatedDerCharges = year1DerCharges * Math.pow(1 + energyEsc, i);
            const escalatedFuelCost = year1FuelCost * Math.pow(1 + energyEsc, i);
            const esaPayment = esaPaymentsArray[i] || 0;
            return {
                year: i + 1, utilitySavings: savings, esaPayment,
                derCharges: escalatedDerCharges, fuelCost: escalatedFuelCost,
                netCashflow: savings - esaPayment - escalatedDerCharges - escalatedFuelCost,
            };
        });

        let effectiveRate = 0;
        if (params.esaModel === 'fixed_rate') effectiveRate = params.esaRate;
        else if (annualProductionKwh > 0) effectiveRate = esaPaymentsArray[0] / annualProductionKwh;

        const year1CustomerSavings = customerAnnualCashflows[0]?.netCashflow || 0;
        const lifetimeSavings = customerAnnualCashflows.reduce((sum, cf) => sum + cf.netCashflow, 0);
        const npvOfSavings = customerAnnualCashflows.reduce((npv, cf, i) =>
            npv + cf.netCashflow / Math.pow(1 + params.discountRate, i + 1), 0
        );
        const rateDiscount = bauRate > 0 && effectiveRate > 0 ? (bauRate - effectiveRate) / bauRate : 0;

        // Cumulative chart data
        const cashFlowChartData = [];
        let cumulative = -systemCost + itcAmount;
        cashFlowChartData.push({ year: 0, cumulative });
        annualCashFlows.forEach((cf, i) => { cumulative += cf; cashFlowChartData.push({ year: i + 1, cumulative }); });

        return {
            dealStructure: 'esa',
            term: params.esaTerm,
            developer: {
                irr: developerReturns.withItc.irr,
                irrWithoutItc: developerReturns.withoutItc.irr,
                npv: developerReturns.withItc.npv,
                payback: developerReturns.withItc.paybackYears,
                year1EsaRevenue: esaRevenue.annual[0]?.revenue || 0,
                totalRevenue: esaRevenue.totalRevenue,
                totalExpenses: esaExpenses.totalExpenses,
                totalProfit: developerReturns.withItc.totalProfit,
            },
            customer: {
                year1Savings: year1CustomerSavings, lifetimeSavings, npvOfSavings,
                effectiveRate, bauRate, rateDiscount,
            },
            cashFlows: {
                annualCashFlows, customerAnnualCashflows,
                chartData: cashFlowChartData, revenue: esaRevenue, expenses: esaExpenses,
            },
            systemCost, itcAmount,
        };
    } catch (error) {
        console.error('ESA computation error:', error);
        return null;
    }
}

// Compute CapEx sale results (mirrors DealAnalysisTab:576-712)
function computeCapexResults(params, data) {
    const {
        systemCost, itcAmount, annualSavings, opex, systemConfig,
        year1DerCharges, year1FuelCost, bauRate, vppGrossMidpoint,
    } = data;
    if (systemCost <= 0) return null;

    try {
        const salePrice = systemCost * (1 + params.capexMarkupPercent / 100);
        const term = params.capexTerm || 25;
        const escalation = params.capexEscalationRate || 0;
        const omEscalation = 0.02;

        // Developer margin
        const internalCosts = {
            totalInternalCost: systemCost,
            itcEligibleCost: systemCost * DEFAULT_ITC_ELIGIBLE_FRACTION,
            itcRate: ITC_RATE,
        };
        const customerItcEligibleAmount = params.customerItcEligible ? salePrice * DEFAULT_ITC_ELIGIBLE_FRACTION : 0;
        const capexMargin = FinancialCalculator.calculateCapexMargin(internalCosts, salePrice, customerItcEligibleAmount);

        const customerItc = params.customerItcEligible ? customerItcEligibleAmount * ITC_RATE : 0;

        // Annual O&M
        const pvOM = opex?.pvOM || 0;
        const batteryOM = opex?.batteryOM || 0;
        const generatorOM = opex?.generatorOM || 0;
        const insurance = opex?.insurance || 0;
        const annualOM = pvOM + batteryOM + generatorOM + insurance;

        // VPP — apply participation mode to gross revenue
        const vppRevenueFactor = params.vppParticipationMode === 'direct' ? 1.0 : (100 - params.vppAggregatorCutPct) / 100;
        const vppAnnualRevenue = params.includeVpp ? vppGrossMidpoint * vppRevenueFactor : 0;

        // Year 0
        const year0CashFlow = -salePrice + customerItc;

        // Year-by-year
        let cumulative = year0CashFlow;
        let customerPayback = null;
        let lifetimeSavings = 0;
        let lifetimeVpp = 0;
        let lifetimeOM = 0;
        let customerNpvSum = year0CashFlow;

        const annualCashFlows = [];
        const cashFlowChartData = [{ year: 0, cumulative }];

        for (let yr = 1; yr <= term; yr++) {
            const savings = annualSavings * Math.pow(1 + escalation, yr - 1);
            const om = annualOM * Math.pow(1 + omEscalation, yr - 1);
            const derCharges = year1DerCharges * Math.pow(1 + escalation, yr - 1);
            const fuelCost = year1FuelCost * Math.pow(1 + escalation, yr - 1);
            let vppRev = 0;
            if (vppAnnualRevenue > 0 && yr <= params.vppDurationYears) {
                vppRev = vppAnnualRevenue * Math.pow(1 + escalation, yr - 1);
            }
            const netCashFlow = savings + vppRev - om - derCharges - fuelCost;
            cumulative += netCashFlow;
            lifetimeSavings += savings;
            lifetimeVpp += vppRev;
            lifetimeOM += om;
            customerNpvSum += netCashFlow / Math.pow(1 + params.discountRate, yr);

            if (customerPayback === null && cumulative >= 0) {
                const prevCum = cumulative - netCashFlow;
                customerPayback = yr - 1 + Math.abs(prevCum) / netCashFlow;
            }

            annualCashFlows.push({
                year: yr, savings, vppRevenue: vppRev, om,
                derCharges, fuelCost, netCashFlow, cumulative,
            });
            cashFlowChartData.push({ year: yr, cumulative });
        }

        const lifetimeNetValue = lifetimeSavings + lifetimeVpp - lifetimeOM - salePrice + customerItc;

        // Customer IRR via Newton-Raphson
        let customerIrr = null;
        const irrCashFlows = [year0CashFlow, ...annualCashFlows.map(cf => cf.netCashFlow)];
        let rate = 0.10;
        for (let iter = 0; iter < 100; iter++) {
            let npvVal = 0, dnpv = 0;
            for (let t = 0; t < irrCashFlows.length; t++) {
                npvVal += irrCashFlows[t] / Math.pow(1 + rate, t);
                dnpv -= t * irrCashFlows[t] / Math.pow(1 + rate, t + 1);
            }
            if (Math.abs(dnpv) < 1e-10) break;
            const newRate = rate - npvVal / dnpv;
            if (Math.abs(newRate - rate) < 1e-6) { customerIrr = newRate; break; }
            rate = newRate;
            if (rate < -0.99 || rate > 10) break;
        }

        return {
            dealStructure: 'capex',
            term,
            developer: {
                salePrice,
                grossMargin: capexMargin.grossMargin,
                grossMarginPercent: capexMargin.grossMarginPercent,
                developerItc: capexMargin.developerItc,
                netMargin: capexMargin.netMargin,
                netMarginPercent: capexMargin.netMarginPercent,
            },
            customer: {
                customerItc,
                payback: customerPayback > 0 ? parseFloat(customerPayback.toFixed(1)) : null,
                npv: customerNpvSum,
                irr: customerIrr,
                lifetimeSavings,
                lifetimeVpp,
                lifetimeOM,
                lifetimeNetValue,
                bauRate,
            },
            cashFlows: {
                annualCashFlows: annualCashFlows.map(cf => cf.netCashFlow),
                detailedAnnualCashFlows: annualCashFlows,
                chartData: cashFlowChartData,
            },
            systemCost, itcAmount,
        };
    } catch (error) {
        console.error('CapEx computation error:', error);
        return null;
    }
}

// Compute full financial results from params + scenario data
function computeResults(params, scenario, helpers) {
    const data = extractScenarioData(scenario, helpers);
    if (params.dealStructure === 'capex') {
        return computeCapexResults(params, data);
    }
    return computeEsaResults(params, data);
}

// Compact numeric input styled for dark theme
function CompactInput({ label, value, onChange, adornment, type = 'number', step, min, max, sx = {} }) {
    return (
        <TextField
            label={label}
            value={value}
            onChange={onChange}
            type={type}
            size="small"
            inputProps={{ step, min, max }}
            InputProps={{
                endAdornment: adornment ? <InputAdornment position="end"><Typography sx={{ fontSize: '0.7rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>{adornment}</Typography></InputAdornment> : undefined,
                sx: {
                    fontSize: '0.8rem',
                    color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                    fontFamily: vortxTypography.mono,
                    '& input': { py: 0.75, px: 1 },
                },
            }}
            InputLabelProps={{
                sx: { fontSize: '0.7rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' },
            }}
            sx={{
                minWidth: 90,
                '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: isRetro ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' },
                    '&:hover fieldset': { borderColor: 'rgba(178,140,80,0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#b28c50' },
                },
                ...sx,
            }}
        />
    );
}

export default function InteractiveEconomicsPanel({
    scenarios,
    getSystemConfig,
    getFinancialSummary,
    getDealAnalysis,
    getCapexBreakdown,
    getOpexBreakdown,
    getCashFlows,
    getDetailedSavings,
    getCommercialAssumptions,
    monthlyPerformanceData,
    economicsSubtab,
    setEconomicsSubtab,
}) {
    const { isRetro } = useThemeMode();
    // Per-scenario editable params, keyed by scenario id
    const [paramsByScenario, setParamsByScenario] = useState(() => {
        const initial = {};
        scenarios.forEach(s => {
            initial[s.id] = extractInitialParams(s);
        });
        return initial;
    });

    const [parametersExpanded, setParametersExpanded] = useState(true);

    // Update a single param for a single scenario
    const updateParam = useCallback((scenarioId, key, value) => {
        setParamsByScenario(prev => ({
            ...prev,
            [scenarioId]: { ...prev[scenarioId], [key]: value },
        }));
    }, []);

    // Compute results per scenario
    const resultsByScenario = useMemo(() => {
        const results = {};
        const helpers = { getSystemConfig, getFinancialSummary, getCapexBreakdown, getOpexBreakdown, getDealAnalysis, monthlyPerformanceData };
        scenarios.forEach(s => {
            const params = paramsByScenario[s.id];
            if (params) {
                results[s.id] = computeResults(params, s, helpers);
            }
        });
        return results;
    }, [paramsByScenario, scenarios, getSystemConfig, getFinancialSummary, getCapexBreakdown, getOpexBreakdown, getDealAnalysis, monthlyPerformanceData]);

    // Detect if any scenario uses ESA or CapEx (for adaptive row labels)
    const anyEsa = scenarios.some(s => paramsByScenario[s.id]?.dealStructure === 'esa');
    const anyCapex = scenarios.some(s => paramsByScenario[s.id]?.dealStructure === 'capex');

    // Table cell style helpers
    const thStyle = (idx) => ({
        textAlign: idx != null ? 'center' : 'left',
        padding: '8px 12px',
        borderBottom: idx != null ? `2px solid ${SCENARIO_COLORS[idx]}` : '2px solid #b28c50',
        fontSize: idx != null ? '0.8rem' : '0.7rem',
        fontWeight: 600,
        color: idx != null ? SCENARIO_COLORS[idx] : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
        textTransform: idx == null ? 'uppercase' : 'none',
    });

    const tdLabel = { padding: '10px 12px', fontSize: '0.85rem', color: isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' };
    const tdValue = (positive) => ({
        textAlign: 'right', padding: '10px 12px', fontSize: '0.9rem',
        fontFamily: vortxTypography.mono,
        color: positive === true ? '#22c55e' : positive === false ? '#ef4444' : isRetro ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
        fontWeight: positive != null ? 600 : 400,
    });

    // Render the model-specific pricing input for a scenario
    const renderPricingInput = (scenarioId, params) => {
        switch (params.esaModel) {
            case 'fixed_rate':
                return (
                    <CompactInput
                        label="Rate"
                        value={params.esaRate}
                        onChange={(e) => updateParam(scenarioId, 'esaRate', parseFloat(e.target.value) || 0)}
                        adornment="$/kWh"
                        step={0.01}
                        min={0}
                        sx={{ minWidth: 110 }}
                    />
                );
            case 'savings_share':
                return (
                    <CompactInput
                        label="Share"
                        value={(params.esaSharePercent * 100).toFixed(0)}
                        onChange={(e) => updateParam(scenarioId, 'esaSharePercent', (parseFloat(e.target.value) || 0) / 100)}
                        adornment="%"
                        step={1}
                        min={0}
                        max={100}
                        sx={{ minWidth: 80 }}
                    />
                );
            case 'fixed_monthly':
                return (
                    <CompactInput
                        label="Payment"
                        value={params.esaPayment}
                        onChange={(e) => updateParam(scenarioId, 'esaPayment', parseFloat(e.target.value) || 0)}
                        adornment="$/yr"
                        step={1000}
                        min={0}
                        sx={{ minWidth: 110 }}
                    />
                );
            case 'capacity_payment':
                return (
                    <CompactInput
                        label="Rate"
                        value={params.esaRatePerKw}
                        onChange={(e) => updateParam(scenarioId, 'esaRatePerKw', parseFloat(e.target.value) || 0)}
                        adornment="$/kW/mo"
                        step={1}
                        min={0}
                        sx={{ minWidth: 120 }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box>
            {/* Economics Subtabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {['developer', 'customer', 'cashflows'].map(tab => (
                    <Button
                        key={tab}
                        onClick={() => setEconomicsSubtab(tab)}
                        sx={{
                            px: 2, py: 0.75, fontSize: '0.75rem', fontWeight: 600,
                            textTransform: 'none',
                            color: economicsSubtab === tab ? '#b28c50' : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                            bgcolor: economicsSubtab === tab ? 'rgba(178,140,80,0.15)' : 'transparent',
                            border: economicsSubtab === tab ? '1px solid rgba(178,140,80,0.3)' : '1px solid transparent',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'rgba(178,140,80,0.1)' },
                        }}
                    >
                        {tab === 'developer' ? 'Developer' : tab === 'customer' ? 'Customer' : 'Cash Flows'}
                    </Button>
                ))}
            </Box>

            {/* Deal Parameters (collapsible) */}
            <Box sx={{
                mb: 3, borderRadius: 1,
                border: '1px solid rgba(178,140,80,0.2)',
                bgcolor: 'rgba(178,140,80,0.05)',
            }}>
                <Box
                    onClick={() => setParametersExpanded(!parametersExpanded)}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1.5, cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(178,140,80,0.08)' },
                    }}
                >
                    <Typography sx={{
                        fontSize: '0.7rem', fontWeight: 600, color: '#b28c50',
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                    }}>
                        Deal Parameters (Interactive)
                    </Typography>
                    <IconButton size="small" sx={{ color: '#b28c50' }}>
                        {parametersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                </Box>

                <Collapse in={parametersExpanded}>
                    <Box sx={{ px: 2, pb: 2 }}>
                        {/* Per-scenario parameter columns */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: scenarios.length <= 2
                                ? `repeat(${scenarios.length}, 1fr)`
                                : `repeat(${scenarios.length}, minmax(220px, 1fr))`,
                            gap: 2,
                            overflowX: 'auto',
                        }}>
                            {scenarios.map((s, idx) => {
                                const params = paramsByScenario[s.id];
                                if (!params) return null;
                                return (
                                    <Box key={s.id} sx={{
                                        p: 1.5, borderRadius: 1,
                                        border: `1px solid ${SCENARIO_COLORS[idx]}33`,
                                        bgcolor: `${SCENARIO_COLORS[idx]}08`,
                                    }}>
                                        <Typography sx={{
                                            fontSize: '0.75rem', fontWeight: 600,
                                            color: SCENARIO_COLORS[idx], mb: 1.5,
                                            borderBottom: `1px solid ${SCENARIO_COLORS[idx]}33`,
                                            pb: 0.5,
                                        }}>
                                            {s.name}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {/* Deal Structure Toggle */}
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {['esa', 'capex'].map(ds => (
                                                    <Button
                                                        key={ds}
                                                        onClick={() => updateParam(s.id, 'dealStructure', ds)}
                                                        size="small"
                                                        sx={{
                                                            flex: 1, py: 0.5, fontSize: '0.7rem', fontWeight: 600,
                                                            textTransform: 'none',
                                                            color: params.dealStructure === ds ? (isRetro ? '#fff' : '#2c2c2c') : isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                            bgcolor: params.dealStructure === ds ? 'rgba(178,140,80,0.4)' : isRetro ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                                            border: params.dealStructure === ds ? '1px solid #b28c50' : isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                                                            borderRadius: 0.5,
                                                            '&:hover': { bgcolor: params.dealStructure === ds ? 'rgba(178,140,80,0.5)' : isRetro ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
                                                        }}
                                                    >
                                                        {ds === 'esa' ? 'ESA' : 'CapEx Sale'}
                                                    </Button>
                                                ))}
                                            </Box>

                                            {/* ESA-specific inputs */}
                                            {params.dealStructure === 'esa' && (<>
                                                <FormControl size="small" fullWidth>
                                                    <InputLabel sx={{ fontSize: '0.7rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>ESA Model</InputLabel>
                                                    <Select
                                                        value={params.esaModel}
                                                        onChange={(e) => updateParam(s.id, 'esaModel', e.target.value)}
                                                        label="ESA Model"
                                                        sx={{
                                                            fontSize: '0.75rem', color: isRetro ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: isRetro ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(178,140,80,0.4)' },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#b28c50' },
                                                        }}
                                                        MenuProps={{ PaperProps: { sx: { bgcolor: isRetro ? '#1a1a1a' : '#fff', color: isRetro ? '#fff' : '#2c2c2c' } } }}
                                                    >
                                                        {Object.entries(PRICING_MODEL_LABELS).map(([key, label]) => (
                                                            <MenuItem key={key} value={key} sx={{ fontSize: '0.75rem' }}>{label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                {renderPricingInput(s.id, params)}

                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <CompactInput
                                                        label="Term"
                                                        value={params.esaTerm}
                                                        onChange={(e) => updateParam(s.id, 'esaTerm', parseInt(e.target.value) || 1)}
                                                        adornment="yr" step={1} min={1} max={30} sx={{ flex: 1 }}
                                                    />
                                                    <CompactInput
                                                        label="Escalation"
                                                        value={(params.esaEscalation * 100).toFixed(1)}
                                                        onChange={(e) => updateParam(s.id, 'esaEscalation', (parseFloat(e.target.value) || 0) / 100)}
                                                        adornment="%" step={0.1} sx={{ flex: 1 }}
                                                    />
                                                </Box>
                                            </>)}

                                            {/* CapEx-specific inputs */}
                                            {params.dealStructure === 'capex' && (<>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <CompactInput
                                                        label="Markup"
                                                        value={params.capexMarkupPercent}
                                                        onChange={(e) => updateParam(s.id, 'capexMarkupPercent', parseFloat(e.target.value) || 0)}
                                                        adornment="%" step={1} sx={{ flex: 1 }}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={params.customerItcEligible}
                                                                onChange={(e) => updateParam(s.id, 'customerItcEligible', e.target.checked)}
                                                                size="small"
                                                                sx={{ color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', '&.Mui-checked': { color: '#b28c50' } }}
                                                            />
                                                        }
                                                        label={<Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>Customer ITC</Typography>}
                                                        sx={{ m: 0 }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <CompactInput
                                                        label="Term"
                                                        value={params.capexTerm}
                                                        onChange={(e) => updateParam(s.id, 'capexTerm', parseInt(e.target.value) || 1)}
                                                        adornment="yr" step={1} min={1} max={30} sx={{ flex: 1 }}
                                                    />
                                                    <CompactInput
                                                        label="Escalation"
                                                        value={(params.capexEscalationRate * 100).toFixed(1)}
                                                        onChange={(e) => updateParam(s.id, 'capexEscalationRate', (parseFloat(e.target.value) || 0) / 100)}
                                                        adornment="%" step={0.1} sx={{ flex: 1 }}
                                                    />
                                                </Box>
                                            </>)}

                                            {/* Shared: Discount Rate */}
                                            <CompactInput
                                                label="Discount Rate"
                                                value={(params.discountRate * 100).toFixed(1)}
                                                onChange={(e) => updateParam(s.id, 'discountRate', (parseFloat(e.target.value) || 0) / 100)}
                                                adornment="%" step={0.5}
                                            />

                                            {/* Shared: VPP Toggle + Detail — only show if scenario has VPP estimate data */}
                                            {(() => {
                                                const cr = s.ui_state?.form?.dealAnalysisConfig?.computedResults;
                                                const hasVpp = (cr?.vppEstimateAnnual || cr?.vppAnnualRevenue) > 0 || cr?.vppDetail;
                                                if (!hasVpp) return null;
                                                const vppDetail = cr?.vppDetail;
                                                const revFactor = params.vppParticipationMode === 'direct' ? 1.0 : (100 - params.vppAggregatorCutPct) / 100;
                                                const fmtCurr = (v) => '$' + Math.round(v).toLocaleString();
                                                const fmtRange = (low, high) => `${fmtCurr(low)} – ${fmtCurr(high)}`;
                                                return (
                                                    <>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={params.includeVpp}
                                                                        onChange={(e) => updateParam(s.id, 'includeVpp', e.target.checked)}
                                                                        size="small"
                                                                        sx={{ '& .Mui-checked': { color: '#b28c50' }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#b28c50' } }}
                                                                    />
                                                                }
                                                                label={<Typography sx={{ fontSize: '0.7rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>VPP Revenue</Typography>}
                                                                sx={{ m: 0 }}
                                                            />
                                                            {params.includeVpp && (
                                                                <CompactInput
                                                                    label="Duration"
                                                                    value={params.vppDurationYears}
                                                                    onChange={(e) => updateParam(s.id, 'vppDurationYears', parseInt(e.target.value) || 1)}
                                                                    adornment="yr" step={1} min={1} max={25} sx={{ flex: 1 }}
                                                                />
                                                            )}
                                                        </Box>
                                                        {params.includeVpp && vppDetail && (
                                                            <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'rgba(178,140,80,0.08)', border: '1px solid rgba(178,140,80,0.2)' }}>
                                                                {/* ISO / Zone */}
                                                                {vppDetail.iso && (
                                                                    <Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 1, fontFamily: vortxTypography.mono }}>
                                                                        {vppDetail.iso} · {vppDetail.zone}
                                                                    </Typography>
                                                                )}
                                                                {/* Participation Mode Toggle */}
                                                                <Box sx={{ mb: 1.5 }}>
                                                                    <Typography sx={{ fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', mb: 0.5 }}>Participation</Typography>
                                                                    <ToggleButtonGroup
                                                                        value={params.vppParticipationMode}
                                                                        exclusive
                                                                        onChange={(_, v) => v && updateParam(s.id, 'vppParticipationMode', v)}
                                                                        size="small"
                                                                        sx={{
                                                                            '& .MuiToggleButton-root': {
                                                                                fontSize: '0.65rem', py: 0.25, px: 1, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                                borderColor: isRetro ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', textTransform: 'none',
                                                                            },
                                                                            '& .Mui-selected': { bgcolor: 'rgba(178,140,80,0.2)', color: '#b28c50' },
                                                                        }}
                                                                    >
                                                                        <ToggleButton value="direct">Self-Managed</ToggleButton>
                                                                        <ToggleButton value="aggregator">Aggregator</ToggleButton>
                                                                    </ToggleButtonGroup>
                                                                    {params.vppParticipationMode === 'aggregator' && (
                                                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Slider
                                                                                value={params.vppAggregatorCutPct}
                                                                                onChange={(_, v) => updateParam(s.id, 'vppAggregatorCutPct', v)}
                                                                                min={5} max={50} step={1}
                                                                                size="small"
                                                                                sx={{ flex: 1, color: '#b28c50', '& .MuiSlider-thumb': { width: 12, height: 12 } }}
                                                                            />
                                                                            <Typography sx={{ fontSize: '0.7rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontFamily: vortxTypography.mono, minWidth: 70, textAlign: 'right' }}>
                                                                                Keep {100 - params.vppAggregatorCutPct}%
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                {/* Net Revenue Summary */}
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#b28c50', mb: 1 }}>
                                                                    Net VPP Revenue: {fmtRange(
                                                                        (vppDetail.grossLow || 0) * revFactor - (vppDetail.cannibalization?.total_lost_savings_low || 0),
                                                                        (vppDetail.grossHigh || 0) * revFactor - (vppDetail.cannibalization?.total_lost_savings_high || 0)
                                                                    )}/yr
                                                                </Typography>
                                                                {/* Revenue Breakdown Table */}
                                                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { fontSize: '0.65rem', py: 0.3, px: 0.5, color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }, '& th': { textAlign: 'left', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' } }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Revenue Stream</th>
                                                                            <th style={{ textAlign: 'right' }}>Annual Est.</th>
                                                                            <th>Basis</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {vppDetail.revenueStreams?.map((stream, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>{stream.name}</td>
                                                                                <td style={{ textAlign: 'right', fontFamily: vortxTypography.mono }}>
                                                                                    {stream.annual_low === 0 && stream.annual_high === 0
                                                                                        ? '\u2014'
                                                                                        : fmtRange(stream.annual_low * revFactor, stream.annual_high * revFactor)
                                                                                    }
                                                                                </td>
                                                                                <td style={{ color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>{stream.basis}</td>
                                                                            </tr>
                                                                        ))}
                                                                        {vppDetail.drPrograms?.map((prog, idx) => (
                                                                            <tr key={`dr-${idx}`}>
                                                                                <td>DR: {prog.name}</td>
                                                                                <td style={{ textAlign: 'right', fontFamily: vortxTypography.mono }}>
                                                                                    {fmtCurr(prog.annual_est * revFactor)}
                                                                                </td>
                                                                                <td style={{ color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>{prog.utility}</td>
                                                                            </tr>
                                                                        ))}
                                                                        {vppDetail.cannibalization && (vppDetail.cannibalization.total_lost_savings_low > 0 || vppDetail.cannibalization.total_lost_savings_high > 0) && (
                                                                            <tr>
                                                                                <td style={{ color: '#e57373' }}>Opportunity Cost</td>
                                                                                <td style={{ textAlign: 'right', fontFamily: vortxTypography.mono, color: '#e57373' }}>
                                                                                    ({fmtRange(vppDetail.cannibalization.total_lost_savings_low, vppDetail.cannibalization.total_lost_savings_high)})
                                                                                </td>
                                                                                <td style={{ color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>Lost peak-shaving</td>
                                                                            </tr>
                                                                        )}
                                                                        <tr style={{ borderTop: '1px solid rgba(178,140,80,0.3)' }}>
                                                                            <td style={{ fontWeight: 700 }}>Net VPP Total</td>
                                                                            <td style={{ textAlign: 'right', fontFamily: vortxTypography.mono, fontWeight: 700, color: '#b28c50' }}>
                                                                                {fmtRange(
                                                                                    (vppDetail.grossLow || 0) * revFactor - (vppDetail.cannibalization?.total_lost_savings_low || 0),
                                                                                    (vppDetail.grossHigh || 0) * revFactor - (vppDetail.cannibalization?.total_lost_savings_high || 0)
                                                                                )}
                                                                            </td>
                                                                            <td />
                                                                        </tr>
                                                                    </tbody>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Collapse>
            </Box>

            {/* DEVELOPER SUBTAB */}
            {economicsSubtab === 'developer' && (
                <Box>
                    <Typography sx={{
                        fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2,
                    }}>
                        Developer Returns (Live)
                    </Typography>
                    <Box sx={{ overflowX: 'auto', mb: 4 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle(null)}>Metric</th>
                                    {scenarios.map((s, idx) => (
                                        <th key={s.id} style={thStyle(idx)}>
                                            {s.name}
                                            <Typography sx={{ fontSize: '0.6rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mt: 0.25 }}>
                                                {paramsByScenario[s.id]?.dealStructure === 'capex' ? 'CapEx Sale' : 'ESA'}
                                            </Typography>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Row: IRR / Gross Margin */}
                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                    <td style={tdLabel}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TimelineIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                            {anyEsa ? 'IRR (with ITC)' : 'Gross Margin'}
                                        </Box>
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(r?.developer.grossMarginPercent > 0)}>
                                                    {r?.developer.grossMarginPercent != null ? fmtPercent(r.developer.grossMarginPercent) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(r?.developer.irr > 0)}>
                                                {r?.developer.irr != null ? fmtPercent(r.developer.irr) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Row: Payback / Net Margin */}
                                <tr>
                                    <td style={tdLabel}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SpeedIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                            {anyEsa ? 'Payback Period' : 'Net Margin (w/ ITC)'}
                                        </Box>
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(r?.developer.netMargin > 0)}>
                                                    {r?.developer.netMargin != null ? fmtCurrency(r.developer.netMargin) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(null)}>
                                                {r?.developer.payback != null ? `${r.developer.payback.toFixed(1)} yrs` : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Row: NPV / Sale Price */}
                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                    <td style={tdLabel}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrendingUpIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                            {anyEsa ? 'NPV' : 'Sale Price'}
                                        </Box>
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(null)}>
                                                    {r?.developer.salePrice != null ? fmtCurrency(r.developer.salePrice) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(r?.developer.npv >= 0)}>
                                                {r?.developer.npv != null ? fmtCurrency(r.developer.npv) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Row: Year 1 ESA Revenue / Developer ITC */}
                                <tr>
                                    <td style={tdLabel}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachMoneyIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                            {anyEsa ? 'Year 1 ESA Revenue' : 'Developer ITC'}
                                        </Box>
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(r?.developer.developerItc > 0)}>
                                                    {r?.developer.developerItc ? fmtCurrency(r.developer.developerItc) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(r?.developer.year1EsaRevenue > 0)}>
                                                {r?.developer.year1EsaRevenue ? fmtCurrency(r.developer.year1EsaRevenue) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* ESA-only rows */}
                                {anyEsa && (<>
                                    <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                        <td style={tdLabel}>Total Revenue</td>
                                        {scenarios.map(s => {
                                            const r = resultsByScenario[s.id];
                                            return (
                                                <td key={s.id} style={tdValue(null)}>
                                                    {r?.dealStructure === 'esa' && r?.developer.totalRevenue ? fmtCurrency(r.developer.totalRevenue) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    <tr>
                                        <td style={tdLabel}>Total Expenses</td>
                                        {scenarios.map(s => {
                                            const r = resultsByScenario[s.id];
                                            return (
                                                <td key={s.id} style={tdValue(false)}>
                                                    {r?.dealStructure === 'esa' && r?.developer.totalExpenses ? fmtCurrency(r.developer.totalExpenses) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </>)}
                                {/* CapEx / ITC summary row */}
                                <tr style={{ backgroundColor: 'rgba(178,140,80,0.1)', borderTop: '2px solid rgba(178,140,80,0.5)' }}>
                                    <td style={{ ...tdLabel, color: '#b28c50', fontWeight: 600 }}>System Cost (Net of ITC)</td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        return (
                                            <td key={s.id} style={{ ...tdValue(null), color: '#b28c50', fontWeight: 700 }}>
                                                {r ? fmtCurrency(r.systemCost - r.itcAmount) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Box>
            )}

            {/* CUSTOMER SUBTAB */}
            {economicsSubtab === 'customer' && (
                <Box>
                    <Typography sx={{
                        fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2,
                    }}>
                        Customer Economics (Live)
                    </Typography>
                    <Box sx={{ overflowX: 'auto', mb: 4 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle(null)}>Metric</th>
                                    {scenarios.map((s, idx) => (
                                        <th key={s.id} style={thStyle(idx)}>{s.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* NPV */}
                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                    <td style={tdLabel}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrendingUpIcon sx={{ fontSize: 18, color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }} />
                                            NPV
                                        </Box>
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        const npv = r?.dealStructure === 'capex' ? r?.customer.npv : r?.customer.npvOfSavings;
                                        return (
                                            <td key={s.id} style={tdValue(npv >= 0)}>
                                                {npv != null ? fmtCurrency(npv) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Payback (CapEx) / Year 1 Net Savings (ESA) */}
                                <tr>
                                    <td style={tdLabel}>
                                        {anyCapex ? 'Payback Period' : 'Year 1 Net Savings'}
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(null)}>
                                                    {r?.customer.payback != null ? `${r.customer.payback.toFixed(1)} yrs` : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(r?.customer.year1Savings >= 0)}>
                                                {r?.customer.year1Savings != null ? fmtCurrency(r.customer.year1Savings) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Customer IRR (CapEx) / Lifetime Savings */}
                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                    <td style={tdLabel}>
                                        {anyCapex ? 'Customer IRR' : 'Lifetime Savings'}
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(r?.customer.irr > 0)}>
                                                    {r?.customer.irr != null ? fmtPercent(r.customer.irr) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(r?.customer.lifetimeSavings >= 0)}>
                                                {r?.customer.lifetimeSavings != null ? fmtCurrency(r.customer.lifetimeSavings) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Customer ITC (CapEx) / Effective Rate (ESA) */}
                                <tr>
                                    <td style={tdLabel}>
                                        {anyCapex ? 'Customer ITC' : 'Effective Rate'}
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(r?.customer.customerItc > 0)}>
                                                    {r?.customer.customerItc ? fmtCurrency(r.customer.customerItc) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(null)}>
                                                {r?.customer.effectiveRate ? `$${r.customer.effectiveRate.toFixed(4)}/kWh` : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {/* Lifetime Net Value (CapEx) / Rate Discount (ESA) */}
                                <tr style={{ backgroundColor: isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                    <td style={tdLabel}>
                                        {anyCapex ? 'Lifetime Net Value' : 'Rate Discount vs. Utility'}
                                    </td>
                                    {scenarios.map(s => {
                                        const r = resultsByScenario[s.id];
                                        if (r?.dealStructure === 'capex') {
                                            return (
                                                <td key={s.id} style={tdValue(r?.customer.lifetimeNetValue >= 0)}>
                                                    {r?.customer.lifetimeNetValue != null ? fmtCurrency(r.customer.lifetimeNetValue) : '—'}
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.id} style={tdValue(r?.customer.rateDiscount > 0)}>
                                                {r?.customer.rateDiscount != null ? fmtPercent(r.customer.rateDiscount) : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Box>
            )}

            {/* CASHFLOWS SUBTAB */}
            {economicsSubtab === 'cashflows' && (
                <Box>
                    <Typography sx={{
                        fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        letterSpacing: '0.1em', textTransform: 'uppercase', mb: 3, textAlign: 'center',
                    }}>
                        Cumulative Cash Flow Projection (Live)
                    </Typography>

                    {/* Chart */}
                    <Box sx={{ height: 300, mb: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={(() => {
                                    // Merge all scenario chart data by year
                                    const maxYears = Math.max(...scenarios.map(s =>
                                        resultsByScenario[s.id]?.cashFlows?.chartData?.length || 0
                                    ));
                                    return Array.from({ length: maxYears }, (_, i) => {
                                        const row = { year: i };
                                        scenarios.forEach((s, idx) => {
                                            const chartData = resultsByScenario[s.id]?.cashFlows?.chartData;
                                            row[`s${idx}`] = chartData?.[i]?.cumulative ?? null;
                                        });
                                        return row;
                                    });
                                })()}
                                margin={{ top: 10, right: 20, left: 20, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={isRetro ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"} />
                                <XAxis
                                    dataKey="year"
                                    tick={{ fontSize: 11, fill: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                                    label={{ value: 'Year', position: 'insideBottom', offset: -10, fontSize: 11, fill: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                                />
                                <RechartsTooltip
                                    contentStyle={{ backgroundColor: isRetro ? '#1a1a1a' : '#fff', border: isRetro ? '1px solid #333' : '1px solid #ddd', fontSize: '0.75rem', color: isRetro ? '#fff' : '#2c2c2c' }}
                                    formatter={(value, name) => [fmtCurrency(value), scenarios[parseInt(name.replace('s', ''))]?.name || name]}
                                    labelFormatter={(year) => `Year ${year}`}
                                />
                                {scenarios.map((s, idx) => (
                                    <Area
                                        key={s.id}
                                        type="monotone"
                                        dataKey={`s${idx}`}
                                        name={`s${idx}`}
                                        stroke={SCENARIO_COLORS[idx]}
                                        fill={`${SCENARIO_COLORS[idx]}20`}
                                        strokeWidth={2}
                                        dot={false}
                                        connectNulls
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Legend */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 4 }}>
                        {scenarios.map((s, idx) => (
                            <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: SCENARIO_COLORS[idx] }} />
                                <Typography sx={{ fontSize: '0.8rem', color: isRetro ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>{s.name}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Year-by-Year Table */}
                    <Typography sx={{
                        fontSize: '0.75rem', fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                        letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2,
                    }}>
                        Year-by-Year Cash Flows
                    </Typography>
                    <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th rowSpan={2} style={{
                                        textAlign: 'left', padding: '8px 12px',
                                        borderBottom: '2px solid #b28c50', fontSize: '0.75rem',
                                        fontWeight: 600, color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                        position: 'sticky', top: 0, backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', zIndex: 2, minWidth: 50,
                                    }}>
                                        Year
                                    </th>
                                    {scenarios.map((s, idx) => (
                                        <th key={s.id} colSpan={3} style={{
                                            textAlign: 'center', padding: '8px 4px',
                                            borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                            fontSize: '0.8rem', fontWeight: 600, color: SCENARIO_COLORS[idx],
                                            position: 'sticky', top: 0, backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', zIndex: 1,
                                        }}>
                                            {s.name}
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {scenarios.map((s, idx) => (
                                        <React.Fragment key={s.id}>
                                            <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', borderLeft: `2px solid ${SCENARIO_COLORS[idx]}` }}>Revenue</th>
                                            <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>Net CF</th>
                                            <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: '0.65rem', color: isRetro ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontWeight: 600, backgroundColor: isRetro ? '#1a1a1a' : '#f5f5f5', position: 'sticky', top: 28, borderBottom: isRetro ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>Cumulative</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const maxTerm = Math.max(...scenarios.map(s => resultsByScenario[s.id]?.term || paramsByScenario[s.id]?.esaTerm || 20));
                                    return Array.from({ length: maxTerm + 1 }, (_, year) => (
                                        <tr key={year} style={{ backgroundColor: year % 2 === 0 ? (isRetro ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)') : 'transparent' }}>
                                            <td style={{ padding: '6px 12px', fontSize: '0.8rem', color: isRetro ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>{year}</td>
                                            {scenarios.map((s, idx) => {
                                                const r = resultsByScenario[s.id];
                                                const borderStyle = `2px solid ${SCENARIO_COLORS[idx]}`;

                                                if (year === 0) {
                                                    const investment = r ? -(r.systemCost - r.itcAmount) : 0;
                                                    return (
                                                        <React.Fragment key={s.id}>
                                                            <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: isRetro ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', borderLeft: borderStyle }}>—</td>
                                                            <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: '#ef4444' }}>{fmtCurrency(investment, true)}</td>
                                                            <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.8rem', fontFamily: vortxTypography.mono, fontWeight: 600, color: '#ef4444' }}>{fmtCurrency(investment, true)}</td>
                                                        </React.Fragment>
                                                    );
                                                }

                                                const revenue = r?.cashFlows?.revenue?.annual?.[year - 1]?.revenue ?? 0;
                                                const netCf = r?.cashFlows?.annualCashFlows?.[year - 1] ?? 0;
                                                const cumulative = r?.cashFlows?.chartData?.[year]?.cumulative;

                                                return (
                                                    <React.Fragment key={s.id}>
                                                        <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: '#22c55e', borderLeft: borderStyle }}>{fmtCurrency(revenue, true)}</td>
                                                        <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.75rem', fontFamily: vortxTypography.mono, color: netCf >= 0 ? '#22c55e' : '#ef4444' }}>{fmtCurrency(netCf, true)}</td>
                                                        <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '0.8rem', fontFamily: vortxTypography.mono, fontWeight: 600, color: cumulative >= 0 ? '#22c55e' : '#ef4444' }}>{cumulative != null ? fmtCurrency(cumulative, true) : '—'}</td>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
